<template>
    <div
        v-if="hasItems"
        class="relative flex flex-col gap-2 pb-3"
    >
        <div
            ref="scrollContainerRef"
            class="flex max-h-[9rem] flex-col gap-0.5 overflow-y-auto [scrollbar-gutter:stable]"
        >
            <MyOrderActionsLiveActivityItem
                v-for="notification in notifications"
                :key="notification.id"
                :notification="notification"
                @click="onItemClick(notification)"
                @dismiss="onItemDismiss(notification)"
            />
            <div
                v-if="isFetchingNextPage"
                class="py-2 text-center text-xxs text-muval-gray-3"
            >
                Loading…
            </div>
        </div>
        <div class="pointer-events-none absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-white to-transparent" />
        <div class="pointer-events-none absolute inset-x-0 bottom-3 h-3 bg-gradient-to-t from-white to-transparent" />
    </div>
</template>

<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import { useNotificationsStore } from '@store/notifications';
import { buildNotificationRoute } from '@utilities/notificationActions';
import { useInfinitePagination } from '@/composables/useInfinitePagination';

const emit = defineEmits<{ (e: 'close'): void }>();

const router = useRouter();
const notificationsStore = useNotificationsStore();
const queryClient = useQueryClient();

const scrollContainerRef = ref<HTMLElement | null>(null);
const visitorQueryKey = computed(() => ['me/notifications', 'visitor', 'unread']);

const { accumulatedData, isFetchingNextPage } = useInfinitePagination({
    url: 'me/notifications',
    queryKey: visitorQueryKey,
    filters: { scope: 'visitor', unread: 1, limit: 10 },
    paginationType: 'page',
    scrollContainer: scrollContainerRef,
});

const notifications = computed(() => accumulatedData.value);
const hasItems = computed(() => notifications.value.length > 0);

watch(
    () => notificationsStore.invalidationCounter,
    () => queryClient.invalidateQueries({ queryKey: visitorQueryKey.value }),
);

function markRead(notification: any) {
    queryClient.setQueriesData<{ pages: { data: any[] }[] }>({ queryKey: visitorQueryKey.value }, (old) => {
        if (!old) return old;
        return {
            ...old,
            pages: old.pages.map((page) => ({
                ...page,
                data: page.data.filter((n) => n.id !== notification.id),
            })),
        };
    });

    notificationsStore.markAsReadOptimistic(notification.id, 'visitor');
}

function onItemClick(notification: any) {
    markRead(notification);
    emit('close');

    const route = buildNotificationRoute(notification);
    if (route) {
        router.push(route);
    }
}

function onItemDismiss(notification: any) {
    markRead(notification);
}
</script>
